import { Duration, DurationObjectUnits } from 'luxon'

import { logger } from '../infrastructure/logging'

/**
 * Returns singular or plural name of the date unit
 * @param {string} unit - name or abbreviation of the date unit ('year', 'y', 'month', 'mo', 'd', etc.)
 * @param {number} [value] - numeric value to detect singular vs. plural
 * @returns {string} "day" vs "days", "month" vs. "months", "year" vs. "years" and do on
 */
function getDateUnitName(unit: string, value = 1) {
  const suffix = value === 1 ? '' : 's'
  switch (unit) {
    case 'y':
    case 'year':
    case 'years':
      return 'year' + suffix
    // Note: ISO duration format doesn't support quarters
    case 'q':
    case 'quarter':
    case 'quarters':
      return 'quarter' + suffix
    case 'm': // Note: remove if we need support for 'minutes' unit in the future
    case 'mo':
    case 'month':
    case 'months':
      return 'month' + suffix
    case 'w':
    case 'week':
    case 'weeks':
      return 'week' + suffix
    case 'd':
    case 'day':
    case 'days':
      return 'day' + suffix
    default:
      logger.warn('getDateUnitName() - unknown date uint:', unit)
      return unit + suffix // We don't know this unit, so just add suffix
  }
}

/**
 * Returns human-readable text for given ISO formatted period/duration
 * @param {string} [durationInIsoFormat] - value to convert as ISO formatted period/duration, 'P2Y3M1D' for example
 * @returns {string} human-readable text, "2 years 3 months 1 day" for example
 */
export function durationToText(durationInIsoFormat = ''): string {
  const units: DurationObjectUnits = Duration.fromISO(durationInIsoFormat).toObject()

  // The order of DurationObjectUnits is years, quarter, months, weeks, days, and so on.
  const texts: string[] = Object.keys(units).map(
    (key) => `${units[key]} ${getDateUnitName(key.toLowerCase(), units[key])}`,
  )

  return texts.length > 0 ? texts.join(' ') : ''
}
